import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

const sections = [
  {
    title: "1. Información que Recopilamos",
    content: [
      "Al utilizar la aplicación Serena, podemos recopilar los siguientes tipos de información:",
      "Información proporcionada por el usuario:",
      "• Nombre y correo electrónico al registrarse.",
      "• Preferencias de bienestar y rituales seleccionados.",
      "• Contenido que genere o comparta dentro de la aplicación.",
      "Información recopilada automáticamente:",
      "• Datos del dispositivo: modelo, sistema operativo, identificador único del dispositivo.",
      "• Datos de uso: páginas visitadas, funciones utilizadas, tiempo de sesión, frecuencia de uso.",
      "• Datos de ubicación aproximada (solo si usted otorga permiso explícito).",
      "• Información de diagnóstico y rendimiento de la aplicación.",
      "Información de terceros:",
      "• Datos de autenticación si inicia sesión mediante Google o Apple.",
      "• Información de procesadores de pago (no almacenamos datos de tarjetas de crédito o débito).",
    ],
  },
  {
    title: "2. Uso de la Información",
    content: [
      "La información recopilada se utiliza para los siguientes fines:",
      "• Proporcionar, mantener y mejorar la experiencia de la aplicación Serena.",
      "• Personalizar el contenido de bienestar (afirmaciones, rituales, sonidos) según sus preferencias.",
      "• Procesar transacciones y enviar confirmaciones de compra.",
      "• Enviar comunicaciones relevantes sobre actualizaciones, promociones y contenido nuevo (con su consentimiento).",
      "• Analizar patrones de uso para mejorar nuestros productos y servicios.",
      "• Detectar, prevenir y resolver problemas técnicos o de seguridad.",
      "• Cumplir con obligaciones legales aplicables.",
    ],
  },
  {
    title: "3. Almacenamiento y Seguridad",
    content: [
      "Serena implementa medidas técnicas y organizativas para proteger su información personal:",
      "• Los datos se almacenan en servidores seguros con cifrado en tránsito (TLS/SSL) y en reposo.",
      "• El acceso a los datos personales está restringido al personal autorizado.",
      "• Realizamos auditorías periódicas de seguridad.",
      "• Los datos se conservan únicamente durante el tiempo necesario para cumplir con los fines descritos en esta política, o según lo exija la ley.",
      "A pesar de nuestros esfuerzos, ningún sistema de transmisión o almacenamiento de datos es completamente seguro. Si detecta una vulnerabilidad de seguridad, le pedimos que nos contacte de inmediato.",
    ],
  },
  {
    title: "4. Compartición de Datos",
    content: [
      "No vendemos su información personal a terceros. Podemos compartir datos en las siguientes circunstancias:",
      "• Proveedores de servicios: Empresas que nos ayudan a operar la aplicación (hosting, análisis, procesamiento de pagos). Estos proveedores están obligados contractualmente a proteger sus datos.",
      "• Cumplimiento legal: Cuando sea requerido por ley, orden judicial o autoridad gubernamental competente.",
      "• Protección de derechos: Para proteger los derechos, propiedad o seguridad de Serena, nuestros usuarios o el público.",
      "• Con su consentimiento: Cuando usted autorice explícitamente compartir información con un tercero específico.",
      "• Transferencia de negocio: En caso de fusión, adquisición o venta de activos, sus datos podrían transferirse como parte de la transacción, previa notificación.",
    ],
  },
  {
    title: "5. Derechos del Usuario",
    content: [
      "De conformidad con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP) de México, usted tiene los siguientes derechos ARCO:",
      "• Acceso: Solicitar información sobre los datos personales que tenemos sobre usted.",
      "• Rectificación: Corregir datos personales inexactos o incompletos.",
      "• Cancelación: Solicitar la eliminación de sus datos personales cuando considere que no están siendo tratados conforme a esta política.",
      "• Oposición: Oponerse al tratamiento de sus datos para fines específicos.",
      "Para ejercer estos derechos, envíe una solicitud a hola@serenacandles.com.mx con el asunto \"Derechos ARCO\". Responderemos en un plazo máximo de 20 días hábiles.",
      "También puede solicitar la eliminación total de su cuenta y datos asociados directamente desde la configuración de la aplicación.",
    ],
  },
  {
    title: "6. Cookies y Tecnologías de Seguimiento",
    content: [
      "La aplicación Serena puede utilizar las siguientes tecnologías:",
      "• Cookies y almacenamiento local: Para recordar sus preferencias y mantener su sesión activa.",
      "• Herramientas de análisis: Para comprender cómo se utiliza la aplicación y mejorar la experiencia del usuario.",
      "• Notificaciones push: Solo si usted las habilita desde la configuración de su dispositivo.",
      "Puede gestionar estas preferencias desde la configuración de la aplicación o de su dispositivo en cualquier momento.",
    ],
  },
  {
    title: "7. Menores de Edad",
    content: [
      "La aplicación Serena no está dirigida a menores de 13 años. No recopilamos intencionalmente información personal de menores.",
      "Si descubrimos que hemos recopilado datos de un menor sin el consentimiento parental verificable, eliminaremos dicha información de inmediato.",
      "Si usted es padre o tutor y cree que su hijo nos ha proporcionado datos personales, contáctenos a hola@serenacandles.com.mx.",
    ],
  },
  {
    title: "8. Cambios a esta Política",
    content: [
      "Serena se reserva el derecho de actualizar esta Política de Privacidad en cualquier momento. Los cambios serán efectivos al publicarse en la aplicación.",
      "Le notificaremos sobre cambios sustanciales a través de la aplicación o por correo electrónico antes de que entren en vigor.",
      "Le recomendamos revisar esta política periódicamente para estar informado sobre cómo protegemos su información.",
    ],
  },
  {
    title: "10. Eliminación de Cuenta y Datos",
    content: [
      "Usted tiene derecho a solicitar en cualquier momento la eliminación total de su cuenta y de todos los datos personales que Serena haya recopilado.",
      "La eliminación incluye:",
      "• Información de registro (nombre, correo electrónico).",
      "• Historial de rituales, preferencias de bienestar y datos de uso.",
      "• Datos del dispositivo e información de diagnóstico asociada a su cuenta.",
      "• Cualquier contenido generado dentro de la aplicación.",
      "Para ejercer este derecho, puede utilizar nuestro formulario de solicitud de eliminación disponible en: /eliminar-cuenta",
      "Procesaremos su solicitud en un plazo máximo de 10 días hábiles. Una vez completada la eliminación, recibirá una confirmación por correo electrónico. Esta acción es permanente e irreversible.",
    ],
  },
  {
    title: "11. Contacto",
    content: [
      "Si tiene preguntas, comentarios o solicitudes relacionadas con esta Política de Privacidad, puede contactarnos a través de:",
      "• Correo electrónico: hola@serenacandles.com.mx",
      "• Sitio web: serenacandles.com.mx",
      "Nos comprometemos a responder sus consultas en un plazo máximo de 10 días hábiles.",
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <div
      className="min-h-screen bg-[#050505] text-white relative noise-overlay"
      data-testid="privacy-page"
    >
      {/* Decorative gold glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/3 blur-[150px] pointer-events-none" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-24 md:py-32">
        {/* Back link */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-[#D4AF37] transition-colors duration-300 mb-12 group"
            aria-label="Volver al inicio"
          >
            <ArrowLeft
              size={16}
              className="group-hover:-translate-x-1 transition-transform duration-300"
            />
            Volver al inicio
          </Link>
        </motion.div>

        {/* Title */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            visible: { transition: { staggerChildren: 0.1 } },
          }}
        >
          <motion.h1
            variants={fadeUp}
            className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] mb-4 text-white"
          >
            Política de{" "}
            <span className="italic text-[#D4AF37]">Privacidad</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="text-sm text-white/30 mb-16 tracking-wide"
          >
            Última actualización: 10 de marzo de 2026
          </motion.p>
        </motion.div>

        {/* Intro */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mb-16"
        >
          <p className="text-base md:text-lg text-white/60 leading-relaxed">
            En Serena, la privacidad de nuestros usuarios es una prioridad
            fundamental. Esta Política de Privacidad describe cómo recopilamos,
            utilizamos, almacenamos y protegemos su información personal cuando
            utiliza nuestra aplicación móvil y servicios asociados.
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-12">
          {sections.map((section, index) => (
            <motion.section
              key={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={fadeUp}
              className="group"
            >
              <h2 className="font-serif text-xl md:text-2xl font-normal tracking-tight mb-5 text-white/90 group-hover:text-[#D4AF37] transition-colors duration-500">
                {section.title}
              </h2>
              <div className="space-y-3 pl-0 md:pl-4 border-l border-white/5 md:border-l-2 md:border-[#D4AF37]/10">
                {section.content.map((paragraph, pIndex) => (
                  <p
                    key={pIndex}
                    className="text-sm md:text-base text-white/50 leading-relaxed pl-4"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </motion.section>
          ))}
        </div>

        {/* Divider */}
        <div className="my-20 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />

        {/* Footer note */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="text-center"
        >
          <p className="text-xs text-white/20 mb-6">
            © 2025 Serena. Todos los derechos reservados. Hecho en México.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#D4AF37]/70 hover:text-[#D4AF37] transition-colors duration-300"
            aria-label="Volver al inicio"
          >
            <ArrowLeft size={14} />
            Volver al inicio
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
